/**
 * Navigation Utility
 * Provides a way for non-React code (like API client) to trigger navigation
 * without using window.location.href directly
 */

import { ROUTES } from "@/constants";

type NavigateFunction = (path: string) => void;

// Store the navigate function from React Router
let navigateRef: NavigateFunction | null = null;

/**
 * Set the navigate function from React Router
 * Call this from the App component or a component that has access to useNavigate
 */
export function setNavigate(navigate: NavigateFunction): void {
  navigateRef = navigate;
}

/**
 * Clear the navigate function (call on unmount)
 */
export function clearNavigate(): void {
  navigateRef = null;
}

/**
 * Navigate to a path using React Router if available, otherwise fall back to
 * window.location for critical redirects like auth failures
 *
 * @param path - The path to navigate to
 * @param options - Navigation options
 */
export function navigate(
  path: string,
  options: { replace?: boolean; fallbackToLocation?: boolean } = {}
): void {
  const { replace = false, fallbackToLocation = true } = options;

  if (navigateRef) {
    // Use React Router navigation
    navigateRef(path);
  } else if (fallbackToLocation) {
    // Fallback to window.location for critical redirects
    // This is acceptable for auth failures where React context may not be available
    if (replace) {
      window.location.replace(path);
    } else {
      window.location.href = path;
    }
  }
}

/**
 * Build the login URL, remembering where the user was so they come back to it
 * after signing in. Uses a `next` query param rather than router state because
 * this path can fall back to window.location (no state channel).
 *
 * Exported for unit tests.
 */
/**
 * True for a path this app may send the browser to after login: a
 * same-origin absolute path and nothing else. A leading "//" is a
 * protocol-relative URL, and a backslash after the slash is one too once
 * the URL parser has had its way ("/\\evil.example" becomes
 * https://evil.example/): react-router's history call throws on it and its
 * fallback is a plain window.location.replace (change review 2026-09-15,
 * portion 3 follow-up). The parser itself is the last word.
 */
export function isSafeReturnPath(path: string | null | undefined): path is string {
  if (!path || !path.startsWith("/") || path.startsWith("//") || path.includes("\\")) {
    return false;
  }
  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    return new URL(path, origin).origin === origin;
  } catch {
    return false;
  }
}

export function loginUrlWithReturnTo(current: string): string {
  // Never bounce back to the login/auth pages themselves, and never off-site:
  // `next` must be a same-origin absolute path.
  const isSafe =
    isSafeReturnPath(current) &&
    !current.startsWith(ROUTES.LOGIN) &&
    !current.startsWith("/change-password") &&
    !current.startsWith("/reset-password") &&
    !current.startsWith("/forgot-password") &&
    current !== "/";
  return isSafe ? `${ROUTES.LOGIN}?next=${encodeURIComponent(current)}` : ROUTES.LOGIN;
}

/**
 * Navigate to login page
 * Used by API client when authentication fails
 */
export function navigateToLogin(): void {
  const current =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : "/";
  navigate(loginUrlWithReturnTo(current), { fallbackToLocation: true });
}

/**
 * Navigate to dashboard
 */
export function navigateToDashboard(): void {
  navigate(ROUTES.DASHBOARD, { fallbackToLocation: true });
}

/**
 * Router state LoginPage attaches to its post-sign-in navigation. The route
 * guards (ProtectedRoute roles / permission) are the one answer to "may this
 * person open that page?", so sign-in returns everyone to where they were
 * and lets the guard decide. A guard refusing a page reached straight from
 * sign-in sends the person home rather than to Unauthorized: it was a stale
 * or shared link, not a page they chose this session.
 */
export const AFTER_SIGN_IN_STATE = { afterSignIn: true } as const;

export function isAfterSignIn(state: unknown): boolean {
  return (state as { afterSignIn?: unknown } | null)?.afterSignIn === true;
}
